import type { SelectMultiChoicelistOption } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { PackageProviderName } from 'data/ppoints/constants';
import { saveAcivatedShippingTypesByProvider } from 'features/settings/components/ppoints/pickup-points/components/pickup-point-settings/mutation/saveAcivatedShippingTypes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useAppSelector } from 'redux/hooks';
import { selectEnabledShippingTypesSelector } from 'redux/slices/enabled-shipping-types/enabled-shipping-types.selector';
import { saveShippingRateMappings } from '../api/shippingRateMappingApi';
import { ShippingRateMapping, ShippingRateMappingsPayload } from '../models/shipping-rate-mapping';
import { mergeShippingRateDetails, shippingRateDetailsOf } from '../utils/shipping-rate-details';
import { MappingCountryMarkets, useMappingCountryMarkets } from './useMappingCountryMarkets';
import { useShippingRateOptionsByCountry } from './useShippingRateOptionsByCountry';

/** Stable identity so a country with no rates doesn't remount its selector on every render. */
const EMPTY_OPTIONS: SelectMultiChoicelistOption<number>[] = [];

export interface ShippingRateMappingForm {
    /** Selected Shopify shipping rate ids, keyed by the mapping's ISO country code. */
    ratesByCountry: Record<string, number[]>;
    /** Enabled shipping type names for this provider, keyed by ISO country code. */
    enabledTypesByCountry: Record<string, string[]>;
}

export interface UseProviderRateMappingDataReturn {
    withMarket: MappingCountryMarkets[];
    withoutMarket: MappingCountryMarkets[];
    /**
     * True only until the first load resolves. Later revalidations — a save, a market intent, the
     * SWR refresh interval — keep the current content on screen instead of flashing a skeleton.
     */
    isInitialLoading: boolean;
    error?: Error;
    /** A failed rate-options request, reported separately so it degrades only the selectors. */
    optionsError?: Error;
    /** The defaults this provider's slice of the form should be reset to once loaded. */
    defaults: ShippingRateMappingForm;
    /** Changes whenever `defaults` does — content-keyed, safe to use as a reset-effect dependency. */
    defaultsSignature: string;
    /** Reads the current selection for a country, for rendering a controlled selector. */
    getRates(countryCode: string): number[];
    /** Writes a country's selection and marks the form dirty, which raises the save bar. */
    setRates(countryCode: string, rateIds: number[]): void;
    /** The rates a country can be mapped to, for rendering its selector. */
    getOptions(countryCode: string): SelectMultiChoicelistOption<number>[];
    /** Whether a shipping type is switched on for a country. */
    isTypeEnabled(countryCode: string, shippingType: string): boolean;
    /** Switches a shipping type on or off for a country, marking the form dirty. */
    setTypeEnabled(countryCode: string, shippingType: string, enabled: boolean): void;
    /**
     * Changes whenever the live selection (`getRates`/`isTypeEnabled`) does — content-keyed, like
     * `defaultsSignature` but for the current form values instead of the reset defaults. A caller
     * that only re-renders on `defaultsSignature` (or on `withMarket`/`withoutMarket` changing) would
     * miss every rate/type toggle, since neither changes when the merchant just picks a rate.
     */
    valuesSignature: string;
    /** Revalidates mappings and markets — e.g. after a market is created through a Shopify intent. */
    mutate(): void;
    /** Revalidates the selectable rates — e.g. after rates are added in the Shopify market editor. */
    mutateOptions(): void;
    /**
     * Persists this provider's slice — the two full-replace PUTs described below. Doesn't reset the
     * form, toast, or revalidate; the caller (a single provider's page, or a group of them sharing
     * one save bar) does that once, after every provider it's saving has succeeded.
     *
     * - `/settings/mappings/shipping/rate/<provider>` takes the rate mappings, rebuilt from every
     *   mapping the GET returned — countries without a market included, so an untouched country keeps
     *   its rates — but only for countries that end up with at least one rate. Clearing a country's
     *   rates drops it from the payload, which is how it gets removed server-side.
     * - `/enabled-shipping-types/<provider>` takes the switches, echoing back the same country list
     *   the enabled-shipping-types GET returned with the merchant's changes applied. Every country is
     *   sent, empty lists included, because omitting one would disable it.
     */
    save(data: ShippingRateMappingForm): Promise<void>;
}

const countryCodeOf = (mapping: ShippingRateMapping): string | undefined =>
    mapping.country?.countryCode?.trim().toUpperCase() || undefined;

/**
 * The API stores rates per shipping type, but the other repos consuming this mapping can't handle
 * that yet — so the UI edits one rate set per country, seeded from the first shipping type.
 */
const initialRatesOf = (mapping: ShippingRateMapping): number[] =>
    mapping.providerRates?.[0]?.shippingRates?.map((rate) => rate.id) ?? [];

/**
 * The data-fetching and payload-building half of a provider's country/rate mapping — everything
 * `useShippingRateMappingForm` does except owning the form and the save bar, so a page that needs
 * one form/save bar shared across several providers (e.g. a wizard step listing every active
 * provider) can drive several of these off a single `useForm`, instead of one save bar per provider.
 *
 * `form`/`fieldPath` let the caller point this at any slice of its own form — pass the bare
 * `ShippingRateMappingForm` field names directly (`fieldPath: (field) => field`) for a form scoped
 * to just this provider, or a nested path (e.g. `` `providers.${provider}.${field}` ``) for a form
 * shared across providers.
 */
export const useProviderRateMappingData = (
    provider: PackageProviderName,
    form: UseFormReturn<Record<string, unknown>>,
    fieldPath: (field: keyof ShippingRateMappingForm) => string = (field) => field
): UseProviderRateMappingDataReturn => {
    const { withMarket, withoutMarket, isLoading, error, mutate } = useMappingCountryMarkets(provider);

    const {
        optionsByCountry,
        ratesById,
        isLoading: isLoadingOptions,
        error: optionsError,
        mutate: mutateOptions,
    } = useShippingRateOptionsByCountry(provider, withMarket);

    const enabledShippingTypes = useAppSelector(selectEnabledShippingTypesSelector);

    const mappings = useMemo(
        () => [...withMarket, ...withoutMarket].map((country) => country.mapping),
        [withMarket, withoutMarket]
    );

    const defaultRatesByCountry = useMemo(
        () =>
            mappings.reduce<Record<string, number[]>>((acc, mapping) => {
                const code = countryCodeOf(mapping);

                if (code) acc[code] = initialRatesOf(mapping);

                return acc;
            }, {}),
        [mappings]
    );

    // Every country the enabled-shipping-types GET returned, narrowed to this provider, so the PUT
    // can send that same list back with only the merchant's changes applied. Mapping countries the
    // GET didn't mention still get an entry, otherwise their switches would have nothing to write to.
    const defaultEnabledTypesByCountry = useMemo(() => {
        const byCountry: Record<string, string[]> = {};

        (enabledShippingTypes ?? []).forEach((entry) => {
            const code = entry.countryCode?.trim().toUpperCase();

            if (!code) return;

            byCountry[code] =
                entry.providers?.find((entryProvider) => entryProvider.name === provider)?.shippingTypes ?? [];
        });

        mappings.forEach((mapping) => {
            const code = countryCodeOf(mapping);

            if (code && !byCountry[code]) byCountry[code] = [];
        });

        return byCountry;
    }, [enabledShippingTypes, mappings, provider]);

    // The PUT echoes the GET's country codes back, so the original casing is preserved rather than
    // the uppercased form keys.
    const countryCodeCasing = useMemo(() => {
        const byUpperCase = new Map<string, string>();

        (enabledShippingTypes ?? []).forEach((entry) => {
            const code = entry.countryCode?.trim();

            if (code) byUpperCase.set(code.toUpperCase(), code);
        });

        return byUpperCase;
    }, [enabledShippingTypes]);

    const defaults = useMemo(
        () => ({ ratesByCountry: defaultRatesByCountry, enabledTypesByCountry: defaultEnabledTypesByCountry }),
        [defaultRatesByCountry, defaultEnabledTypesByCountry]
    );

    // Keyed on the *content*, not the object identity: SWR revalidates on an interval and hands back
    // a fresh array each time, and resetting on identity alone would throw away unsaved edits every
    // time that fired.
    const defaultsSignature = useMemo(() => JSON.stringify(defaults), [defaults]);

    // The PUT wants each rate's full details (name, type, currency, tiers, free-from threshold) next
    // to its id. A freshly picked rate is only known from the market rates; a rate already saved but
    // since removed from the market only from the mapping. See `mergeShippingRateDetails`.
    const rateDetails = useMemo(() => mergeShippingRateDetails(mappings, ratesById), [mappings, ratesById]);

    const save = useCallback(
        async (data: ShippingRateMappingForm) => {
            const payload: ShippingRateMappingsPayload = mappings
                .map((mapping) => {
                    const code = countryCodeOf(mapping);
                    const selected = code ? data.ratesByCountry[code] : undefined;

                    return {
                        mapping,
                        rates: selected ?? mapping.providerRates?.[0]?.shippingRates?.map((rate) => rate.id) ?? [],
                    };
                })
                // Only countries that actually have rates are sent. Since the endpoint is a full
                // replace, omitting a country is how it gets cleared server-side.
                .filter(({ rates }) => rates.length > 0)
                .map(({ mapping, rates }) => ({
                    countryId: mapping.country?.id,
                    providerRates: mapping.providerRates.map((providerRate) => ({
                        shippingType: providerRate.shippingType,
                        // Until the consuming repos support per-shipping-type rates, every type of a
                        // country carries the same set.
                        shippingRates: rates.map((id) => shippingRateDetailsOf(rateDetails, id)),
                    })),
                }));

            // The same country list the enabled-shipping-types GET returned, with the switches applied.
            // It's a full replace too, so every country has to be sent — omitting one disables it.
            const countryShippingTypes = Object.entries(data.enabledTypesByCountry).map(
                ([countryCode, shippingTypes]) => ({
                    countryCode: countryCodeCasing.get(countryCode) ?? countryCode,
                    shippingTypes,
                })
            );

            await saveShippingRateMappings(provider, payload);
            await saveAcivatedShippingTypesByProvider({ countryShippingTypes }, provider);
        },
        [mappings, rateDetails, provider, countryCodeCasing]
    );

    const ratesByCountry = form.watch(fieldPath('ratesByCountry')) as Record<string, number[]> | undefined;

    const getRates = useCallback((countryCode: string) => ratesByCountry?.[countryCode] ?? [], [ratesByCountry]);

    // Once the content has rendered, no revalidation may take it away again. The options request is
    // part of the first load too — without it the selectors would briefly claim the country has no
    // rates before the options arrive.
    const hasLoadedOnce = useRef(false);

    useEffect(() => {
        if (!isLoading && !isLoadingOptions) hasLoadedOnce.current = true;
    }, [isLoading, isLoadingOptions]);

    const getOptions = useCallback(
        (countryCode: string) => optionsByCountry[countryCode] ?? EMPTY_OPTIONS,
        [optionsByCountry]
    );

    const enabledTypesByCountry = form.watch(fieldPath('enabledTypesByCountry')) as
        | Record<string, string[]>
        | undefined;

    const isTypeEnabled = useCallback(
        (countryCode: string, shippingType: string) =>
            Boolean(enabledTypesByCountry?.[countryCode]?.includes(shippingType)),
        [enabledTypesByCountry]
    );

    const setTypeEnabled = useCallback(
        (countryCode: string, shippingType: string, enabled: boolean) => {
            const path = fieldPath('enabledTypesByCountry');
            const current = form.getValues(path) as Record<string, string[]> | undefined;
            const forCountry = current?.[countryCode] ?? [];

            form.setValue(
                path,
                {
                    ...current,
                    [countryCode]: enabled
                        ? [...new Set([...forCountry, shippingType])]
                        : forCountry.filter((type) => type !== shippingType),
                },
                { shouldDirty: true }
            );
        },
        [form, fieldPath]
    );

    const setRates = useCallback(
        (countryCode: string, rateIds: number[]) => {
            const path = fieldPath('ratesByCountry');

            form.setValue(
                path,
                { ...(form.getValues(path) as Record<string, number[]> | undefined), [countryCode]: rateIds },
                { shouldDirty: true }
            );
        },
        [form, fieldPath]
    );

    const valuesSignature = useMemo(
        () => JSON.stringify({ ratesByCountry, enabledTypesByCountry }),
        [ratesByCountry, enabledTypesByCountry]
    );

    return {
        withMarket,
        withoutMarket,
        isInitialLoading: (isLoading || isLoadingOptions) && !hasLoadedOnce.current,
        error,
        optionsError,
        defaults,
        defaultsSignature,
        valuesSignature,
        getRates,
        setRates,
        getOptions,
        isTypeEnabled,
        setTypeEnabled,
        mutate,
        mutateOptions,
        save,
    };
};
